export type PrimeOrderRowStatus = "completed" | "expired" | null;

export type PrimeOrderServiceType =
  | "trade"
  | "europe"
  | "philippines"
  | "canada";

interface EventDetailLike {
  eventId?: string | number | null;
  timestamp?: string | null;
}

interface PrimeOrderRowData {
  eventDetails?: EventDetailLike[];
  transferType?: string | null;
}

const getLatestEventId = (eventDetails?: EventDetailLike[]): string | null => {
  if (!eventDetails?.length) return null;

  const sorted = [...eventDetails].sort(
    (a, b) =>
      new Date(b.timestamp || 0).getTime() -
      new Date(a.timestamp || 0).getTime(),
  );

  const eventId = sorted[0].eventId;
  return eventId != null ? String(eventId) : null;
};

/** Canada External terminal states */
export const CANADA_COMPLETED_EVENT_IDS = new Set(["7"]);
export const CANADA_EXPIRED_EVENT_IDS = new Set(["8", "9", "11", "12"]);

/** Europe External terminal states */
export const EUROPE_EXTERNAL_COMPLETED_EVENT_IDS = new Set(["13"]);
export const EUROPE_EXTERNAL_EXPIRED_EVENT_IDS = new Set(["7", "8", "9", "10"]);

/** Europe Internal terminal states */
export const EUROPE_INTERNAL_COMPLETED_EVENT_IDS = new Set(["5"]);
export const EUROPE_INTERNAL_EXPIRED_EVENT_IDS = new Set(["7", "8", "9", "10"]);

export const isInternalTransfer = (transferType?: string | null) =>
  (transferType || "").toLowerCase().includes("internal");

export const getEuropeTerminalSets = (transferType?: string | null) => {
  if (isInternalTransfer(transferType)) {
    return {
      completed: EUROPE_INTERNAL_COMPLETED_EVENT_IDS,
      expired: EUROPE_INTERNAL_EXPIRED_EVENT_IDS,
    };
  }
  return {
    completed: EUROPE_EXTERNAL_COMPLETED_EVENT_IDS,
    expired: EUROPE_EXTERNAL_EXPIRED_EVENT_IDS,
  };
};

const TRADE_COMPLETED_EVENT_IDS = new Set(["4", "6"]);
const TRADE_EXPIRED_EVENT_ID = "9";

const PHILIPPINES_COMPLETED_EVENT_ID = "4";
const PHILIPPINES_EXPIRED_EVENT_ID = "9";

export const getPrimeOrderRowStatus = (
  serviceType: PrimeOrderServiceType,
  data: PrimeOrderRowData,
): PrimeOrderRowStatus => {
  const latestEventId = getLatestEventId(data.eventDetails);
  if (!latestEventId) return null;

  switch (serviceType) {
    case "trade":
      if (latestEventId === TRADE_EXPIRED_EVENT_ID) return "expired";
      if (TRADE_COMPLETED_EVENT_IDS.has(latestEventId)) return "completed";
      return null;
    case "europe": {
      const { completed, expired } = getEuropeTerminalSets(data.transferType);

      if (expired.has(latestEventId)) return "expired";
      if (completed.has(latestEventId)) return "completed";
      return null;
    }
    case "philippines":
      if (latestEventId === PHILIPPINES_EXPIRED_EVENT_ID) return "expired";
      if (latestEventId === PHILIPPINES_COMPLETED_EVENT_ID) return "completed";
      return null;
    case "canada":
      if (CANADA_EXPIRED_EVENT_IDS.has(latestEventId)) return "expired";
      if (CANADA_COMPLETED_EVENT_IDS.has(latestEventId)) return "completed";
      return null;
    default:
      return null;
  }
};

export const getPrimeOrderAgRowClass = (
  serviceType: PrimeOrderServiceType,
  data: PrimeOrderRowData,
): string => {
  const status = getPrimeOrderRowStatus(serviceType, data);
  if (status === "expired") return "ag-row-expired";
  if (status === "completed") return "ag-row-completed";
  return "";
};
